import { useQuery } from '@apollo/client';
import type { FieldProps } from 'formik';
import { Field as FormikField } from 'formik';
import React, { useState } from 'react';
import Button from '../../components/Button';
import { Field } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import SVGIcon from '../../components/SVGIcon';
import arrowRightIcon from '../../icons/non-prefilled/16x16/arrowRight.svg';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import TopicPills from '../../topic/TopicPills';
import useAllTopics from '../../topic/useAllTopics';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import * as styles from './GithubRepoTopicEditor.module.scss';
import { gql } from '../../graphql/generated/gql';

const GITHUB_REPOS_QUERY = gql(`
  query getGithubRepos {
    repos: gitRepos(platform: GITHUB) {
      owner
      name
    }
  }
`);

const SEARCH_CONFIG = { topic: {} };

interface GithubRepoTopicEditorProps {
  name: string;
}

const GithubRepoTopicEditor = ({
  name: fieldName,
}: GithubRepoTopicEditorProps) => {
  const { data, loading } = useQuery(GITHUB_REPOS_QUERY);
  const repos = data?.repos ?? [];
  const topics = useAllTopics({ flatten: true });
  const [topicEditingAt, setTopicEditingAt] = useState<string | null>(null);

  if (loading) {
    return <LoadingIndicator delay={0} />;
  }

  if (!repos.length) {
    return <>No GitHub repo found.</>;
  }

  return (
    <>
      {repos.map(({ owner, name: repoName }) => {
        const fullName = `${owner}/${repoName}`;
        const isEditing = topicEditingAt === fullName;

        return (
          <div className={styles.repoRow} key={fullName}>
            <div className={styles.checkbox}>
              <Field
                label={
                  <span className={styles.label} title={fullName}>
                    {fullName}
                  </span>
                }
                margin="none"
                name={fieldName}
                type="checkbox"
                value={fullName}
              />
            </div>
            <FormikField name={`topicIdMap.${fullName}`}>
              {({
                field: { name, value },
                form: { values, setFieldValue },
              }: FieldProps<string>) => {
                if (!values[fieldName].includes(fullName)) {
                  return null;
                }

                const topic = value && topics.find(({ id }) => id === value);

                return (
                  <>
                    <div className={styles.arrow}>
                      <SVGIcon inline size="small">
                        {arrowRightIcon}
                      </SVGIcon>
                    </div>
                    {isEditing ? (
                      <div className={styles.select}>
                        <ClickOutsideHandler
                          onClickOutside={() => setTopicEditingAt(null)}
                        >
                          <EntitySelect
                            autoFocus
                            config={SEARCH_CONFIG}
                            onChange={(val) => {
                              if (val) {
                                setFieldValue(name, val.id);
                                setTopicEditingAt(null);
                              }
                            }}
                            styles={{
                              menu: (base) =>
                                isSize(Sizes.SM)
                                  ? base
                                  : { ...base, width: 280 },
                            }}
                          />
                        </ClickOutsideHandler>
                      </div>
                    ) : (
                      <div className={styles.value}>
                        {topic ? (
                          <TopicPills
                            topics={[topic]}
                            onRemove={() => {
                              setFieldValue(name, null);
                              setTopicEditingAt(fullName);
                            }}
                          />
                        ) : (
                          <Button
                            className={styles.uncategorized}
                            onClick={() => setTopicEditingAt(fullName)}
                          >
                            Uncategorized
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            </FormikField>
          </div>
        );
      })}
    </>
  );
};

export default GithubRepoTopicEditor;
